import {
  AddItemToBasketDocument,
  type AddItemToBasketMutation,
  type AddItemToBasketMutationVariables,
  type BasketFragment,
  type BasketItemInput,
  type MenuItemFragment,
  RemoveItemFromBasketDocument,
  type RemoveItemFromBasketMutation,
  type RemoveItemFromBasketMutationVariables,
  UpdateBasketDocument,
  type UpdateBasketMutation,
  type UpdateBasketMutationVariables,
} from '@/generated/graphql'
import { useApolloClient } from '@apollo/client'

import { useCallback } from 'react'

import { createMenuItems, type MenuItem } from '../../domain/menu-item'
import {
  GET_LIVE_MENU_ITEM,
  type LiveMenuItemQueryData,
  type LiveMenuItemQueryVariables,
} from '../../states/vendorMenuQueries'
import { useBasketStateStore } from '../../store/basketStateStore'
import { useCartReorderActions, useCartSnackbarActions } from '../../store/cartStore'
import { useCategoryItemsStore } from '../../store/categoryItemsStore'
import type { PastOrderForReorder } from './use-past-orders'

type LoadOrderArgs = {
  order: PastOrderForReorder
  activeBasket: BasketFragment
}

type ReorderLoaderConfig = {
  isStandingOrderTemplate?: boolean
  editSessionId?: string | null
}

export const useReorderLoader = ({
  isStandingOrderTemplate = false,
  editSessionId = null,
}: ReorderLoaderConfig = {}) => {
  const apolloClient = useApolloClient()
  const { setReorderLoadState, setReorderLastResult } = useCartReorderActions()
  const { showSnackbar, showErrorSnackbar } = useCartSnackbarActions()
  const { setAddingItem, setUpdatingItem } = useBasketStateStore()

  // Resolve a live MenuItem by id. The reorder flow runs without the user
  // having browsed categories, so the lazy category store is usually empty —
  // we therefore query the server by id, falling back to a warm store hit.
  const findLiveMenuItem = useCallback(
    async (id: string): Promise<MenuItem | undefined> => {
      const cached = useCategoryItemsStore.getState().fullItemsByCategory
      for (const categoryId in cached) {
        const found = cached[categoryId]?.find((mi) => mi._id === id)
        if (found) return found
      }

      const { data } = await apolloClient.query<LiveMenuItemQueryData, LiveMenuItemQueryVariables>({
        query: GET_LIVE_MENU_ITEM,
        variables: { id },
        fetchPolicy: 'cache-first',
      })

      const fragment = data.liveMenuItem
      if (!fragment) return undefined
      return createMenuItems([fragment as MenuItemFragment])[0]
    },
    [apolloClient],
  )

  const loadOrder = useCallback(
    async ({ order, activeBasket }: LoadOrderArgs) => {
      const items = order.orderItems.items
      const total = items.length
      let unavailable = 0
      // Tracked across the try/catch so the error snackbar can report how many
      // items made it in before a failure.
      let loaded = 0

      setReorderLoadState('loading', order.id)
      // Mirror the shared loading state so isDataUpdating reflects the bulk
      // operation — without this ReorderBasketFooter.checkoutDisabled stays false
      // and the user can click Checkout while items are still flowing in.
      setAddingItem(true)
      setUpdatingItem(true)

      try {
        // Resolve every past item to a BasketItemInput. The findLiveMenuItem
        // lookups are independent reads, so run them in parallel rather than
        // one-at-a-time (the old sequential loop was the main cause of the
        // slow, item-by-item load). Items the server can't resolve (removed
        // from the menu) are dropped and counted as unavailable.
        const resolved = await Promise.all(
          items.map(async (pastItem): Promise<BasketItemInput | null> => {
            // Past item already flagged unavailable by the server
            if (!pastItem.liveMenuItem) return null

            // Resolve the full domain MenuItem (server query, store fallback)
            const liveMenuItem = await findLiveMenuItem(pastItem.liveMenuItem._id)
            if (!liveMenuItem) return null

            // Reconstruct the option selections against the LIVE menu item so
            // mandatory option groups aren't dropped. A past option might no
            // longer exist on the menu (option removed / renamed): keep the
            // ones we can resolve and rely on backend validation for any
            // resulting min-required violations.
            //
            // Match by optionReference (the optionItem id) — matching by the
            // underlying menu-item id is ambiguous when a category contains
            // multiple option-items that wrap the same item.
            const pastOptions = pastItem.orderItemOptions ?? []
            const resolvedOptions = pastOptions.flatMap((pastOption) => {
              const option = liveMenuItem.options.find((o) => o._id === pastOption.optionCategoryId)
              if (!option) return []
              const optionItem =
                option.items.find((oi) => oi._id === pastOption.optionReference) ??
                option.items.find((oi) => oi.item._id === (pastOption.liveMenuItem?._id ?? ''))
              if (!optionItem) return []
              return [{ option, optionItem, qty: pastOption.qty }]
            })

            return {
              acknowledgedAt: new Date(),
              item: liveMenuItem._id,
              note: pastItem.note ?? null,
              options: resolvedOptions.map(({ option, optionItem, qty }) => ({
                categoryId: option._id,
                item: optionItem.item._id,
                optionItemId: optionItem._id,
                qty,
              })),
              qty: pastItem.qty ?? 1,
            }
          }),
        )

        const itemPayloads = resolved.filter((i): i is BasketItemInput => i !== null)
        unavailable = total - itemPayloads.length

        // Standing-order templates / edit sessions live in a separate backend
        // collection that updateGmBasket can't reach (it only touches plain
        // baskets and would write to the wrong entity, dropping the template
        // association + editSessionId). For those, keep the original
        // sequential add path. Normal baskets use a single bulk updateGmBasket
        // call below, which replaces the items array atomically — no removal
        // loop needed and no item-by-item round trips.
        if (isStandingOrderTemplate || editSessionId) {
          const referencesToRemove = activeBasket.items.map((i) => i.itemReference)
          for (const itemReference of referencesToRemove) {
            await apolloClient.mutate<
              RemoveItemFromBasketMutation,
              RemoveItemFromBasketMutationVariables
            >({
              mutation: RemoveItemFromBasketDocument,
              variables: {
                basketId: activeBasket.id,
                editSessionId: editSessionId ?? null,
                isStandingOrderTemplate: Boolean(isStandingOrderTemplate),
                itemReference,
              },
            })
          }

          for (const itemPayload of itemPayloads) {
            await apolloClient.mutate<AddItemToBasketMutation, AddItemToBasketMutationVariables>({
              mutation: AddItemToBasketDocument,
              variables: {
                basketId: activeBasket.id,
                editSessionId: editSessionId ?? null,
                isStandingOrderTemplate: Boolean(isStandingOrderTemplate),
                itemPayload,
              },
            })
            // Count per success so a mid-loop failure reports the partial
            // progress (e.g. "3 of 5 items added") in the catch below.
            loaded++
          }
        } else {
          // Bulk replace: one round trip, items set wholesale on the basket.
          await apolloClient.mutate<UpdateBasketMutation, UpdateBasketMutationVariables>({
            mutation: UpdateBasketDocument,
            variables: {
              basketId: activeBasket.id,
              basketPayload: { items: itemPayloads },
            },
          })
          // All-or-nothing: a failure throws before this, leaving loaded at 0.
          loaded = itemPayloads.length
        }

        setReorderLastResult({ loaded, unavailable, total })

        if (loaded === 0 && unavailable === total) {
          showSnackbar('error', 'All items from this order are no longer available', 5000)
        } else if (unavailable > 0) {
          showSnackbar(
            'itemAdded',
            `${loaded} ${loaded === 1 ? 'item' : 'items'} loaded, ${unavailable} unavailable`,
            5000,
          )
        } else {
          showSnackbar('itemAdded', `${loaded} ${loaded === 1 ? 'item' : 'items'} loaded`, 4000)
        }

        setReorderLoadState('success', null)
        setTimeout(() => setReorderLoadState('idle', null), 200)
      } catch (error) {
        console.error('Reorder load failed', error)
        setReorderLoadState('error', null)
        showErrorSnackbar(`Couldn't load order — ${loaded} of ${total} items added`)
        setTimeout(() => setReorderLoadState('idle', null), 200)
      } finally {
        setAddingItem(false)
        setUpdatingItem(false)
      }
    },
    [
      apolloClient,
      editSessionId,
      isStandingOrderTemplate,
      setReorderLoadState,
      setReorderLastResult,
      showSnackbar,
      showErrorSnackbar,
      findLiveMenuItem,
      setAddingItem,
      setUpdatingItem,
    ],
  )

  return { loadOrder }
}
